import { IconArrowDownRight, IconArrowUpRight, type IconProps } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { ComponentType, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Group, Skeleton, Text, ThemeIcon } from "@/components/ui";
import { Sparkline } from "./Sparkline";

/**
 * Tarjeta de KPI · alineada al diseño de referencia: rounded-2xl, chip de ícono
 * arriba-derecha (+ flecha de drill si navega), valor extrabold + subtítulo a la
 * izquierda, sparkline (o línea plana si no hay serie) a la derecha.
 *
 * Usa tokens semánticos (bg-card/border-border/text-foreground/muted) para que
 * el modo oscuro siga funcionando; el sparkline lleva datos REALES.
 */
export function StatCard({
  label,
  value,
  delta = null,
  hint,
  icon: Icon,
  color,
  loading,
  deltaLabel = "vs semana pasada",
  to,
  search,
  sparkShape,
  serieColor,
}: {
  label: string;
  value: string;
  delta?: number | null;
  hint?: ReactNode;
  icon: ComponentType<IconProps>;
  color: string;
  loading: boolean;
  deltaLabel?: string;
  to?: string;
  search?: Record<string, unknown>;
  sparkShape?: "a" | "b" | "c" | "d" | "flat";
  serieColor?: string;
}) {
  const contenido = (
    <div
      className={cn(
        "flex h-full flex-col rounded-2xl border border-border bg-card p-4 shadow-2xs transition-all",
        to && "cursor-pointer hover:shadow-xs",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1.5">
          {to ? <IconArrowUpRight size={14} className="text-muted-foreground/60" aria-hidden="true" /> : null}
          <ThemeIcon variant="light" color={color} size="sm">
            <Icon size={14} stroke={1.9} />
          </ThemeIcon>
        </div>
      </div>

      <div className="mt-1 min-w-0">
        {loading ? (
          <Skeleton height={28} width={70} />
        ) : (
          <div className="text-2xl font-extrabold tracking-tight text-ink-900 tabular-nums lining-nums dark:text-ink-100">
            {value}
          </div>
        )}
        {loading ? null : delta !== null && delta !== undefined ? (
          <div className="mt-0.5">
            <DeltaBadge delta={delta} label={deltaLabel} />
          </div>
        ) : hint ? (
          <div className="mt-0.5 text-[11px] font-medium text-muted-foreground">{hint}</div>
        ) : null}
      </div>

      {/* Sparkline decorativo · a lo ancho completo (centrado) y anclado al fondo. */}
      <Sparkline shape={sparkShape} color={serieColor} className="mt-auto h-14 w-full pt-3" />
    </div>
  );

  if (!to) return contenido;
  return (
    <Link to={to as never} search={search as never} aria-label={`${label}: ${value}`} className="block h-full">
      {contenido}
    </Link>
  );
}

function DeltaBadge({ delta, label }: { delta: number; label: string }) {
  if (delta === 0) {
    return (
      <Text size="xs" c="dimmed">
        Sin cambio {label}
      </Text>
    );
  }
  const positivo = delta > 0;
  const Icono = positivo ? IconArrowUpRight : IconArrowDownRight;
  const color = positivo ? "exito" : "peligro";
  return (
    <Group gap={4}>
      <ThemeIcon size="xs" radius="xl" variant="filled" color={color}>
        <Icono size={10} />
      </ThemeIcon>
      <Text size="xs" c={`${color}-600`} fw={500} className="tabular-nums lining-nums">
        {positivo ? "+" : ""}
        {delta}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}
